import React from 'react';
import { RowActions, type RowAction } from '@/components/ui';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../../../hooks/usePermissions';
import type { WorkIdRecord } from '../workIdService';
import { taskPageUrl } from '../utils/taskPageUrl';
import { isLowOrMediumPriority } from '../utils/priorityDisplay';

interface WorkIdActionsProps {
  record: WorkIdRecord;
  onEdit?: (record: WorkIdRecord) => void;
  onApprove?: (record: WorkIdRecord) => void;
  onCancel?: (record: WorkIdRecord) => void;
  onReopen?: (record: WorkIdRecord) => void;
  onReopenTask?: (record: WorkIdRecord) => void;
  onAssignExisting?: (record: WorkIdRecord) => void;
  onCreateNewTask?: (record: WorkIdRecord) => void;
  onUnassign?: (record: WorkIdRecord) => void;
  onMerge?: (record: WorkIdRecord) => void;
  onViewMergeSuggestions?: (record: WorkIdRecord) => void;
  onUnmerge?: (record: WorkIdRecord) => void;
  onReviewHistory?: (record: WorkIdRecord) => void;
  onExportToPdf?: (record: WorkIdRecord) => void;
  onReportAccomplishment?: (record: WorkIdRecord) => void;
  onCloseTask?: (record: WorkIdRecord) => void;
  onCloseWithoutAction?: (record: WorkIdRecord) => void;
  exportingId: number | null;
}

const WorkIdActions: React.FC<WorkIdActionsProps> = ({
  record,
  onEdit,
  onApprove,
  onCancel,
  onReopen,
  onReopenTask,
  onAssignExisting,
  onCreateNewTask,
  onUnassign,
  onMerge,
  onViewMergeSuggestions,
  onUnmerge,
  onReviewHistory,
  onExportToPdf,
  onReportAccomplishment,
  onCloseTask,
  onCloseWithoutAction,
  exportingId
}) => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const hasWorkIdPerm = (key: string) => hasPermission(`workId:${key}`);

  const canApprove = hasWorkIdPerm('approve');
  const canCancel = hasWorkIdPerm('cancel');
  const canAssign = hasWorkIdPerm('assign');
  const canMerge = hasWorkIdPerm('merge');
  const canCloseWithoutAction = hasWorkIdPerm('close_without_action');
  const canEdit = hasWorkIdPerm('create') || hasWorkIdPerm('approve');
  const canReviewHistory = hasWorkIdPerm('review_history');
  const canExport = hasWorkIdPerm('view');

  const status = record.status?.toUpperCase() || '';
  const showEdit = canEdit && (status === 'NEW' || status === 'APPROVED');
  const showApprove = canApprove && status === 'NEW';
  const showCancel = canCancel && status !== 'CANCELLED';
  const showReopen =
    canCancel && (status === 'CANCELLED' || status === 'CLOSED_WITHOUT_ACTION');
  const showReopenTask = canApprove && status === 'CLOSED';
  const showAssign = canAssign && status === 'APPROVED';
  const showUnassign = canAssign && status === 'ASSIGNED' && (record.tasks?.length ?? 0) > 0;
  const showMerge = canMerge && (status === 'NEW' || status === 'APPROVED' || status === 'ASSIGNED');
  const showUnmerge = canMerge && status === 'MERGED';
  const showReviewHistory = canReviewHistory;

  const linkedTask = record.tasks?.length === 1 ? record.tasks[0] : null;
  const showGoToTask = status === 'ASSIGNED' && linkedTask && (linkedTask.type === 'Routine' || linkedTask.type === 'Quantified');
  const taskStatus = linkedTask?.status?.toUpperCase() ?? '';
  const showReportAccomplishment =
    status === 'ASSIGNED' &&
    linkedTask?.type === 'Quantified' &&
    (taskStatus === 'ACTIVE' || taskStatus === 'ACTIVE_MARKED_AS_COMPLETE') &&
    hasPermission('quantified:accomplishment_submit') &&
    !!onReportAccomplishment;
  const showCloseTask =
    status === 'ASSIGNED' &&
    linkedTask?.type === 'Routine' &&
    taskStatus === 'ACTIVE' &&
    canAssign &&
    !!onCloseTask;
  const showCloseWithoutAction =
    canCloseWithoutAction &&
    (status === 'NEW' || status === 'APPROVED') &&
    isLowOrMediumPriority(record.priority) &&
    !!onCloseWithoutAction;

  const hasAny =
    showEdit ||
    showGoToTask ||
    showApprove ||
    showCancel ||
    showReopen ||
    showReopenTask ||
    showAssign ||
    showUnassign ||
    showMerge ||
    showUnmerge ||
    showReviewHistory ||
    showReportAccomplishment ||
    showCloseTask ||
    showCloseWithoutAction ||
    canExport;

  if (!hasAny) {
    return <span className="tw-text-content-muted">-</span>;
  }

  // Every Work ID action collapses into the single kebab menu (maxInline defaults to 0), matching
  // the app-standard layout — no bespoke popover math, so the shared Dropdown's collision-aware
  // portal is what fixes the clipping the tester saw inside the scrolling table container.
  const actions: RowAction[] = [
    { kind: 'edit', onClick: () => onEdit?.(record), hidden: !(showEdit && onEdit) },
    {
      kind: 'custom',
      icon: 'bi-clipboard-check',
      label: 'Report Accomplishment',
      onClick: () => onReportAccomplishment?.(record),
      hidden: !(showReportAccomplishment && onReportAccomplishment),
    },
    {
      kind: 'custom',
      icon: 'bi-check2-square',
      label: 'Close',
      onClick: () => onCloseTask?.(record),
      hidden: !(showCloseTask && onCloseTask),
    },
    {
      kind: 'custom',
      icon: 'bi-box-arrow-up-right',
      label: linkedTask?.type === 'Routine' ? 'Go to Routine Work' : 'Go to Quantified Work',
      onClick: () => linkedTask && navigate(taskPageUrl(linkedTask, true)),
      hidden: !showGoToTask,
    },
    {
      kind: 'custom',
      icon: 'bi-check-circle',
      variant: 'outline-success',
      label: 'Approve',
      onClick: () => onApprove?.(record),
      hidden: !(showApprove && onApprove),
    },
    { kind: 'cancel', onClick: () => onCancel?.(record), hidden: !(showCancel && onCancel) },
    {
      kind: 'custom',
      icon: 'bi-x-circle',
      label: 'Close without action',
      onClick: () => onCloseWithoutAction?.(record),
      hidden: !(showCloseWithoutAction && onCloseWithoutAction),
    },
    {
      kind: 'custom',
      icon: 'bi-arrow-counterclockwise',
      label: 'Reopen',
      onClick: () => onReopen?.(record),
      hidden: !(showReopen && onReopen),
    },
    {
      kind: 'custom',
      icon: 'bi-arrow-counterclockwise',
      label: 'Reopen task',
      onClick: () => onReopenTask?.(record),
      hidden: !(showReopenTask && onReopenTask),
    },
    {
      kind: 'custom',
      icon: 'bi-link-45deg',
      label: 'Assign to Existing Task',
      onClick: () => onAssignExisting?.(record),
      hidden: !(showAssign && onAssignExisting),
    },
    {
      kind: 'custom',
      icon: 'bi-plus-circle',
      label: 'Create New Task and Assign',
      onClick: () => onCreateNewTask?.(record),
      hidden: !(showAssign && onCreateNewTask),
    },
    {
      kind: 'custom',
      icon: 'bi-dash-circle',
      label: 'Remove from Task',
      onClick: () => onUnassign?.(record),
      hidden: !(showUnassign && onUnassign),
    },
    {
      kind: 'custom',
      icon: 'bi-diagram-3',
      label: 'View merge suggestions',
      onClick: () => onViewMergeSuggestions?.(record),
      hidden: !(record.hasMergeSuggestions && status !== 'MERGED' && onViewMergeSuggestions),
    },
    {
      kind: 'custom',
      icon: 'bi-arrows-collapse',
      label: 'Merge',
      onClick: () => onMerge?.(record),
      hidden: !(showMerge && onMerge),
    },
    {
      kind: 'custom',
      icon: 'bi-arrows-expand',
      label: 'Unmerge',
      onClick: () => onUnmerge?.(record),
      hidden: !(showUnmerge && onUnmerge),
    },
    {
      kind: 'history',
      label: 'Review History',
      onClick: () => onReviewHistory?.(record),
      hidden: !(showReviewHistory && onReviewHistory),
    },
    {
      kind: 'download',
      label: exportingId === record.id ? 'Exporting...' : 'Export to PDF',
      onClick: () => onExportToPdf?.(record),
      disabled: exportingId === record.id,
      hidden: !(canExport && onExportToPdf),
    },
  ];

  return <RowActions actions={actions} />;
};

export default WorkIdActions;
